import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Plus, Edit2, Trash2, Link2, FileCog } from 'lucide-react';
import { toast } from 'sonner';

const FILE_TYPES = [
  { value: 'CAD', label: 'CAD Drawing' },
  { value: 'Schematic', label: 'Schematic' },
  { value: 'Weld_Drawing', label: 'Weld / Fabrication Drawing' },
  { value: 'Photo', label: 'Photo / Reference Image' },
  { value: 'Datasheet', label: 'Datasheet' },
  { value: 'Other', label: 'Other' },
];

const EMPTY = { name: '', file_type: 'CAD', label_pattern: '', url_pattern: '', default_revision: '', active: true, description: '' };

/** iter77 — Admin panel for managing engineering-linked-file URL templates. */
const BomLinkedFileTemplatesPanel = ({ api, canEdit = true }) => {
  const [items, setItems] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const { data } = await api().get(`/bom-linked-file-templates${showInactive ? '?include_inactive=true' : ''}`);
      setItems(data || []);
    } catch (e) { toast.error('Failed to load templates'); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [showInactive]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '', file_type: t.file_type || 'CAD',
      label_pattern: t.label_pattern || '', url_pattern: t.url_pattern || '',
      default_revision: t.default_revision || '', active: !!t.active,
      description: t.description || '',
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api().put(`/bom-linked-file-templates/${editing.id}`, form);
        toast.success('Template updated');
      } else {
        await api().post('/bom-linked-file-templates', form);
        toast.success('Template created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api().delete(`/bom-linked-file-templates/${t.id}`);
      toast.success('Deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Card className="industrial-card" data-testid="bom-linked-file-templates-panel">
      <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
        <div>
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-blue-600" /> BOM Linked-File Templates
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Reusable patterns for engineering files. Use placeholders: <code className="text-[10px] bg-slate-100 px-1 rounded">{'{bom_variant_code}'}</code>, <code className="text-[10px] bg-slate-100 px-1 rounded">{'{revision}'}</code>, <code className="text-[10px] bg-slate-100 px-1 rounded">{'{product_type}'}</code>, <code className="text-[10px] bg-slate-100 px-1 rounded">{'{date}'}</code>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-blue-500" data-testid="templates-show-inactive" />
            <span>Show inactive</span>
          </label>
          {canEdit && (
            <Button size="sm" onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="bom-template-add-btn">
              <Plus className="w-4 h-4 mr-1" /> New Template
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 italic p-6 text-center">No templates yet. Click <strong>New Template</strong> to define your first one.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>URL Pattern</TableHead>
                <TableHead>Label Pattern</TableHead>
                <TableHead className="text-center">Active</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(t => (
                <TableRow key={t.id} data-testid={`bom-template-row-${t.id}`} className={t.active ? '' : 'opacity-60'}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <FileCog className="w-4 h-4 text-blue-600" /> {t.name}
                    </div>
                    {t.description && <p className="text-[11px] text-slate-500 mt-0.5">{t.description}</p>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{t.file_type}</Badge></TableCell>
                  <TableCell className="mono text-xs max-w-xs truncate" title={t.url_pattern}>{t.url_pattern || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-xs max-w-xs truncate" title={t.label_pattern}>{t.label_pattern || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${t.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {t.active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`bom-template-edit-${t.id}`}><Edit2 className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(t)} className="text-red-600" data-testid={`bom-template-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="bom-template-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Default CAD Drawing" className="rounded-sm" data-testid="bom-template-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">File Type *</Label>
                <Select value={form.file_type} onValueChange={(v) => setForm({ ...form, file_type: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="bom-template-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FILE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Label Pattern</Label>
              <Input value={form.label_pattern} onChange={(e) => setForm({ ...form, label_pattern: e.target.value })} placeholder="e.g., {bom_variant_code} rev {revision} CAD" className="rounded-sm" data-testid="bom-template-label-pattern-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">URL Pattern</Label>
              <Input value={form.url_pattern} onChange={(e) => setForm({ ...form, url_pattern: e.target.value })} placeholder="https://cad.company.com/{bom_variant_code}-{revision}.pdf" className="rounded-sm mono text-xs" data-testid="bom-template-url-pattern-input" />
              <p className="text-[10px] text-slate-500">Placeholders: <code className="bg-slate-100 px-1 rounded">{'{bom_variant_code}'}</code> <code className="bg-slate-100 px-1 rounded">{'{revision}'}</code> <code className="bg-slate-100 px-1 rounded">{'{product_type}'}</code> <code className="bg-slate-100 px-1 rounded">{'{date}'}</code></p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Default Revision</Label>
                <Input value={form.default_revision || ''} onChange={(e) => setForm({ ...form, default_revision: e.target.value })} placeholder="(leave empty to use BOM revision)" className="rounded-sm mono" data-testid="bom-template-default-rev-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Active</Label>
                <div className="h-9 flex items-center">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="bom-template-active-switch" />
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-sm text-sm" data-testid="bom-template-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-slate-900" data-testid="bom-template-submit-btn">
              {saving ? 'Saving…' : (editing ? 'Save' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BomLinkedFileTemplatesPanel;
